/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useEffect, useState, useRef } from "react";
import usePortal from "react-useportal";
import { ModuleRegistry, provideGlobalGridOptions } from "ag-grid-community";
import { AllEnterpriseModule, IntegratedChartsModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Form, Cascader, Input, Button, Space } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import DivE from "../react/DivE.tsx";
import ModalWindow from "../react/ModalWindow.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getAppState, changeAppState, getLng, getDict, getUser, getSafePtr, getTheme, getCompact, getDesktopType } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  disableCheckbox?: boolean;
  children?: Option[];
}

interface ModalGridRow {
  colName:string;
  title:string;
  path:Array<string>;
  filterText:string;
  selected:boolean;
  disabled?:boolean;
  isLeaf:boolean;
}

type Tcascader = {
  allowClear:boolean,
  autoFocus:boolean,
  variant:"outlined" | "borderless" | "filled",
  changeOnSelect:boolean,
  expandTrigger:"click" | "hover",
  placement:"bottomLeft" | "bottomRight" | "topLeft" | "topRight",
  size:"large" | "middle" | "small",
  multiple:boolean,
  maxTagCount:number | "responsive",
  showSearch?:boolean, // by default - single: false, multiple: true
  selectOnModalButton?:boolean,
  modalWidth?:number,
  modalHeight?:number,
  modalFullscreen?:boolean
};

const { rz } = window;

ModuleRegistry.registerModules([
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);
provideGlobalGridOptions({ theme: "legacy"});

const RZcascader:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZcascader.displayName = "Cascader";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const compact = useAppSelector(getCompact);
  const isMobile = useAppSelector(getDesktopType);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalSearch, setModalSearch] = useState<string>("");
  const modalGridRef = useRef<AgGridReact<any>>(null);

  const {rootAttributes, key, fullPath, type, content, hasitems} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  const {icon, iconClass, iconColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState?.userStyle, elState?.visible, permissions]);

  const rico = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (icon) { ico = (<AppIcons key={key + "-icon"} icopath={icon} attrs={{className: iconClass, color: iconColor}} />) }
    return ico;
  }, [key, icon, iconClass, iconColor]);

  const cascaderType = useMemo<Tcascader>(() => {
    const typ:Tcascader = {
      allowClear: true, autoFocus: false, variant: "outlined", changeOnSelect: false, expandTrigger: "click", placement: "bottomLeft", size: "middle",
      multiple: false, maxTagCount: "responsive", modalFullscreen: isMobile
    };
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["large", "middle", "small"].indexOf(tt)) { typ.size = tt }
      else if (tt === "disallowClear") { typ.allowClear = false }
      else if (tt === "autoFocus") { typ.autoFocus = true }
      else if (~["noborders", "borderless"].indexOf(tt)) { typ.variant = "borderless" }
      else if (tt === "changeOnSelect") { typ.changeOnSelect = true }
      else if (tt === "multiple") { typ.multiple = true }
      else if (~["hover", "click"].indexOf(tt)) { typ.expandTrigger = tt }
      else if (~["bottomLeft", "bottomRight", "topLeft", "topRight"].indexOf(tt)) { typ.placement = tt }
      else if (tt.startsWith("maxTagCount-")) { typ.maxTagCount = Number(tt.split("-")[1]) }
      else if (tt === "showSearch") { typ.showSearch = true }
      else if (~["selectOnModal", "selectOnModalButton"].indexOf(tt)) { typ.selectOnModalButton = true }
      else if (~["modalFullscreen", "modalfullscreen"].indexOf(tt)) { typ.selectOnModalButton = true; typ.modalFullscreen = true }
    });

    if (attributes?.size && ~["large", "middle", "small"].indexOf(attributes.size)) { typ.size = attributes.size }
    if (attributes?.variant) { typ.variant = attributes.variant }
    if (attributes?.trigger && ~["click", "hover"].indexOf(attributes.trigger)) { typ.expandTrigger = attributes.trigger }
    if (attributes?.multiple) { typ.multiple = (attributes.multiple === "true") }
    if (attributes?.allowClear || attributes?.allowclear) { typ.allowClear = (attributes.allowClear === "true" || attributes.allowclear === "true") }
    if (attributes?.maxTagCount) { typ.maxTagCount = (attributes.maxTagCount === "responsive" ? attributes.maxTagCount : Number(attributes.maxTagCount)) }
    if (attributes?.showSearch === "true") { typ.showSearch = true } else if (attributes?.showSearch === "false") { typ.showSearch = false }
    if (attributes?.placement && ~["bottomLeft", "bottomRight", "topLeft", "topRight"].indexOf(attributes.placement)) { typ.placement = attributes.placement }
    if (attributes?.selectOnModalButton) { typ.selectOnModalButton = (attributes.selectOnModalButton === "true") }
    else if (attributes?.selectOnModal) { typ.selectOnModalButton = (attributes.selectOnModal === "true") }
    if (attributes?.modalFullscreen) { typ.modalFullscreen = (attributes.modalFullscreen === "true") }
    if (attributes?.modalWidth) { typ.modalWidth = Number(attributes.modalWidth) }
    if (attributes?.modalHeight) { typ.modalHeight = Number(attributes.modalHeight) }

    return typ;
  }, [isMobile, type, attributes]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const placeholder = useMemo<string | undefined>(() => (elState.attributes["placeholder" + lng] || elState.attributes.placeholder || elState.attributes["placeholder" + rlng]), [elState.attributes, lng]);

  const items = useMemo<Array<Option>>(() => {
    let itms:Array<Option> = [];

    const getSubContent = (cnt) => {
      if (!cnt || !rz.isArray(cnt) || !cnt.length) { return undefined }
      return cnt.map((mm) => {
        const perm1 = fnc.checkPermissions(user, {...mm?.attributes, rootAttributes: {key: rootAttributes.key}}), state1 = appState[mm?.attributes?.fullPath || mm?.fullPath];
        const key1 = mm.key || rz.strMakeID(16);
        let icon1:React.ReactElement | undefined;
        if (mm?.attributes?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.attributes.icon} attrs={{className: mm.attributes?.iconClass || "rz-margin-right-5", color: mm.attributes?.iconColor}} />) }
        if (mm?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.icon} attrs={{className: mm?.iconClass || "rz-margin-right-5", color: mm?.iconColor}} />) }
        const label1 = (
          rz.isString(mm) ? mm : (mm?.attributes ? (
            mm.attributes["title_" + lng] || mm.attributes.title || mm.attributes["title_" + rlng] || mm.attributes["label_" + lng] || mm.attributes.label || mm.attributes["label_" + rlng] || ""
          ) : (
            mm["title_" + lng] || mm.title || mm["title_" + rlng] || mm["label_" + lng] || mm.label || mm["label_" + rlng] || ""
          )
          ));
        return {
          key: key1, value: (rz.isString(mm) ? mm : (mm.value || key1)),
          label: (icon1 && !label1 ? icon1 : (!icon1 && label1 ? label1 : (
            <React.Fragment key={key1 + "-frgm"}>
              {icon1}{label1 ? <span key={key1 + "-ttl"}>{label1}</span> : undefined}
            </React.Fragment>
          ))),
          disabled: (state1?.disabled || perm1.disabled === true), disableCheckbox: (state1?.disabled || perm1.disabled === true),
          children: getSubContent(mm?.children)
        };
      });
    };

    if (elState?.items) {
      if (rz.isArray(elState?.items)) { itms = getSubContent(elState?.items) }
      else if (rz.isString(elState?.items)) { itms = (elState.items.split("|")).map((value) => ({value, label: value, key: rz.strMakeID(16)})) }
    }
    else if (hasitems && content && rz.isArray(content)) { itms = getSubContent(content) }
    return itms;
  }, [appState, content, elState.items, hasitems, lng, rlng, rootAttributes.key, user]);

  const selValue = useMemo<Option | Array<Option> | undefined>(() => {
    let defval:Option | Array<Option>;
    const vlu = elState?.value;
    if (vlu) {
      if (rz.isArray(vlu)) { defval = vlu.map((value) => (value)) } // vlu.filter((value) => (~rz.arrFindObj(items, {value})))
      else if (rz.isString(vlu)) { defval = (vlu.replace((/\//igm), "|").split("|")).map((value) => (value)) }
    }
    // @ts-ignore
    // if (!cascaderType.multiple && defval && defval.length) { return defval[0] }
    // @ts-ignore
    return defval;
  }, [elState?.value]); // cascaderType.multiple

  const selectedPaths = useMemo<Array<Array<string>>>(() => {
    const normalizePath = (arr:any) => ((rz.isArray(arr) ? arr.map((vv) => String(vv)) : []) as Array<string>);
    const vlu:any = selValue;
    if (!vlu) return [];
    if (rz.isArray(vlu)) {
      if (vlu.length && rz.isArray(vlu[0])) {
        return vlu.map((pp:any) => (normalizePath(pp))).filter((pp:Array<string>) => (pp.length));
      }
      const onePath = normalizePath(vlu);
      return (onePath.length ? [onePath] : []);
    }
    return [];
  }, [selValue]);

  const selectedPathKeys = useMemo<Array<string>>(() => (selectedPaths.map((pp) => (pp.join("|")))), [selectedPaths]);

  const modalGridRows = useMemo<Array<ModalGridRow>>(() => {
    const selectedSet = new Set(selectedPathKeys);
    const rows:Array<ModalGridRow> = [];
    const walk = (arr?:Array<Option>, parentPath:Array<string> = [], parentTitles:Array<string> = []) => {
      if (!arr || !arr.length) return;
      arr.forEach((it) => {
        const curTitle = (rz.isString(it.label) ? it.label : String(it.value));
        const curPath = [...parentPath, String(it.value)];
        const curTitlePath = [...parentTitles, curTitle];
        const isLeaf = (!it?.children || !it.children.length);
        if (isLeaf) {
          const isSelectedByParentOrLeaf = selectedPaths.some((sp) => (sp.length <= curPath.length && sp.every((sv, si) => (curPath[si] === sv))));
          rows.push({
            colName: String(it.value),
            title: curTitle,
            path: curPath,
            filterText: (curTitlePath.join(" / ") + " | " + curPath.join(" / ")),
            selected: (isSelectedByParentOrLeaf || selectedSet.has(curPath.join("|"))),
            disabled: Boolean(it.disabled || it.disableCheckbox),
            isLeaf
          });
        }
        if (it?.children && it.children.length) { walk(it.children, curPath, curTitlePath) }
      });
    };
    walk(items);
    return rows;
  }, [items, selectedPathKeys, selectedPaths]);

  const modalGridColumnDefs = useMemo(() => ([
    {field: "colName", hide: true},
    {field: "title", hide: true},
    {field: "filterText", hide: true}
  ]), []);

  const valueTitleMap = useMemo<{[key:string]:string}>(() => {
    const mpp:{[key:string]:string} = {};
    const walk = (arr?:Array<Option>) => {
      if (!arr || !arr.length) return;
      arr.forEach((it) => {
        const key1 = String(it.value || "");
        const title1 = (rz.isString(it.label) ? it.label : key1);
        if (key1 && title1) { mpp[key1] = title1 }
        if (it?.children && it.children.length) { walk(it.children) }
      });
    };
    walk(items);
    return mpp;
  }, [items]);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: ({ru: "Элементы", en: "Items", tr: "Öğeler"}[String(lng || "en")] || "Items"),
    minWidth: 360,
    cellRendererParams: {
      suppressCount: true,
      suppressDoubleClickExpand: true,
      suppressEnterExpand: true,
      innerRenderer: (params) => {
        const key1 = String(params?.value || "");
        return (valueTitleMap[key1] || key1);
      }
    }
  }), [lng, valueTitleMap]);

  const getDataPath = useCallback((rowData:ModalGridRow) => (rowData?.path || []), []);

  const rowSelection_checkboxesMultyRow = useMemo(() => {
    if (cascaderType.multiple) {
      return {
        mode: "multiRow" as const, checkboxes: true, headerCheckbox: true,
        enableMultiSelectWithClick: true, enableClickSelection: true, enableSelectionWithoutKeys: true,
        groupSelects: "filteredDescendants" as const
      };
    }
    return {
      mode: "singleRow" as const, checkboxes: true, headerCheckbox: false,
      enableMultiSelectWithClick: false, enableClickSelection: true,
      groupSelects: "filteredDescendants" as const
    };
  }, [cascaderType.multiple]);

  const onChange = useCallback((value:any, selectedOptions:any) => {
    if (fnc.config?.isDev) console.log({action: "<Cascader key='" + key + "'> -> onChange()", value, selectedOptions});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value, specialParams: {...elState.specialParams, selectedOptions}}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, selectedOptions, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, selectedOptions, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const showModalButtonClick = useCallback(() => {
    if (fnc.config?.isDev) console.log({action: "<Cascader key='" + key + "'> -> showModalButtonClick()"});
    setShowModal(true);
  }, [key]);

  const applyModalSelection = useCallback(() => {
    const selectedLeafPathKeys = new Set<string>();
    modalGridRef.current?.api?.forEachNode((node:any) => {
      if (node?.data?.isLeaf !== true) { return }
      if (!node.isSelected()) { return }
      const path = (rz.isArray(node?.data?.path) ? node.data.path.map((vv:any) => String(vv)) : []);
      if (path.length) { selectedLeafPathKeys.add(path.join("|")) }
    });

    const compressSelectedPaths = (
      arr?:Array<Option>,
      parentPath:Array<string> = []
    ):{allLeavesSelected:boolean, hasLeaves:boolean, paths:Array<Array<string>>} => {
      if (!arr || !arr.length) { return {allLeavesSelected: false, hasLeaves: false, paths: []} }

      let allLeavesSelected = true;
      let hasLeaves = false;
      let paths:Array<Array<string>> = [];

      arr.forEach((it) => {
        const curPath = [...parentPath, String(it.value)];
        const hasChildren = Boolean(it?.children && it.children.length);

        if (!hasChildren) {
          hasLeaves = true;
          const isSelected = selectedLeafPathKeys.has(curPath.join("|"));
          if (isSelected) { paths.push(curPath) }
          else { allLeavesSelected = false }
          return;
        }

        const childAns = compressSelectedPaths(it.children, curPath);
        if (!childAns.hasLeaves) {
          allLeavesSelected = false;
          return;
        }

        hasLeaves = true;
        if (childAns.allLeavesSelected) {
          paths.push(curPath);
        }
        else {
          paths = [...paths, ...childAns.paths];
        }
        if (!childAns.allLeavesSelected) { allLeavesSelected = false }
      });

      return {allLeavesSelected, hasLeaves, paths};
    };

    const finalPaths = compressSelectedPaths(items).paths;

    if (cascaderType.multiple) {
      onChange(finalPaths, []);
    }
    else {
      onChange((finalPaths[0] || []), []);
    }
    setShowModal(false);
  }, [cascaderType.multiple, items, onChange]);

  // eslint-disable-next-line destructuring/no-rename
  const { ref: divRef, openPortal, closePortal, isOpen, Portal } = usePortal({closeOnOutsideClick: true, closeOnEsc: true});
  const portalRef = useRef(null);

  useEffect(() => {
    if (showModal && !isOpen) { openPortal() }
    else if (!showModal && isOpen) { closePortal() }
  }, [closePortal, isOpen, openPortal, showModal]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // if (iType.rules) { frmItemProps.name = String(key); frmItemProps.rules = iType.rules; frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  const mainCascader = (
    <Cascader
      key={key}
      style={styl}
      className={clsu.join(" ")}
      uk-tooltip={tooltip}
      options={items}
      // @ts-ignore
      value={selValue}
      // initialValue={elState.value}
      suffixIcon={rico}
      onChange={onChange}
      placeholder={placeholder}
      showSearch={cascaderType.showSearch}
      changeOnSelect={cascaderType.changeOnSelect}
      placement={cascaderType.placement}
      allowClear={cascaderType.allowClear}
      autoFocus={cascaderType.autoFocus}
      variant={cascaderType.variant}
      maxTagCount={cascaderType.maxTagCount}
      multiple={cascaderType.multiple}
      size={cascaderType.size}
      disabled={elState?.disabled || permissions.disabled === true}
      hidden={elState?.visible === false || permissions.visible === false}
      expandTrigger={cascaderType.expandTrigger}
    />
  );

  if (cascaderType.selectOnModalButton) {
    return (
      wrapperForm(
        <DivE key={key + "-parent-div"} uk-tooltip={tooltip}>
          <Space.Compact key={key + "-space-compact"} block style={{height: "auto"}}>
            {mainCascader}
            <Button key={key + "-show-modal-button"}
              type={"primary"} size={cascaderType.size || "middle"}
              uk-tooltip={{ru: "Выбрать элементы в списке на модальном окне", en: "Select items in the list on the modal window", tr: "Modal pencerede listedeki öğeleri seçin"}[lng || "en"]}
              style={{minWidth: "2em", paddingTop: 0, paddingBottom: 0, height: "auto"}}
              disabled={elState?.disabled || permissions.disabled === true}
              onClick={showModalButtonClick}
            >
              <AppIcons key={key + "-show-modal-button-icon"} icopath={"tb/TbWindowMaximize"} style={{}} attrs={{size: "1.4em"}} />
            </Button>
          </Space.Compact>
          <div key={key + "-dummy-div"} ref={divRef} />
          {isOpen && cascaderType.selectOnModalButton && (
            <Portal key={key + "-portal"} ref={portalRef} style={{height: "100vh", width: "100vw"}} >
              <ModalWindow modalKey={key}
                isOpen={Boolean(showModal)} onRequestClose={() => { setShowModal(false) }}
                initWidth={cascaderType.modalWidth || 720} initHeight={cascaderType.modalHeight || 580} minWidth={420} minHeight={420}
                title={dct.p.selectItems} icon={ <AppIcons key={key + "-modal-icon"} icopath={"tb/TbWindowMaximize"} style={{}} attrs={{}} />}
                dct={dct} closeOnEsc={true} hasResizeable={!isMobile && cascaderType.modalFullscreen !== true} hasMoveable={!isMobile && cascaderType.modalFullscreen !== true}
                isMaximize={isMobile || cascaderType.modalFullscreen === true} hasMaximize={!isMobile && cascaderType.modalFullscreen !== true}
                className="uk-flex uk-flex-center uk-flex-middle rz-theme-d3" zIndex={1500}
              >
                <div className="uk-flex uk-flex-column" style={{height: "100%", width: "100%"}}>
                  <div style={{padding: 12, paddingBottom: 8}}>
                    <Input.Search
                      allowClear
                      value={modalSearch}
                      onChange={(e) => {
                        const { value } = e.target;
                        setModalSearch(value);
                        modalGridRef.current?.api?.setGridOption("quickFilterText", value);
                      }}
                      placeholder={(lng === "ru" ? "Поиск по элементам" : (lng === "tr" ? "Öğelerde ara" : "Search items"))}
                    />
                  </div>
                  <div style={{flex: 1, minHeight: 0, padding: "0 12px 12px 12px"}}>
                    <div
                      className={"ag-theme-" + (compact ? "balham" : "alpine") + (theme === "dark" ? "-dark" : "")}
                      style={{height: "100%", width: "100%"}}
                    >
                      <AgGridReact
                        ref={modalGridRef}
                        rowData={modalGridRows}
                        columnDefs={modalGridColumnDefs}
                        treeData={true}
                        autoGroupColumnDef={autoGroupColumnDef}
                        getDataPath={getDataPath}
                        rowSelection={rowSelection_checkboxesMultyRow}
                        includeHiddenColumnsInQuickFilter={true}
                        groupDefaultExpanded={1}
                        getRowId={(params) => String((params?.data?.path || []).join("|"))}
                        onGridReady={() => {
                          if (modalSearch) { modalGridRef.current?.api?.setGridOption("quickFilterText", modalSearch) }
                        }}
                        onFirstDataRendered={() => {
                          setTimeout(() => {
                            modalGridRef.current?.api?.forEachNode((node) => { node.setSelected(Boolean(node?.data?.selected)) });
                          }, 500);
                        }}
                      />
                    </div>
                  </div>
                  <div className="uk-flex" style={{justifyContent: "space-between", gap: 8, padding: 12, borderTop: "1px solid var(--rz-border-color, #e8e8e8)"}}>
                    <Button onClick={() => { setShowModal(false) }}>
                      {lng === "ru" ? "Отмена" : (lng === "tr" ? "İptal" : "Cancel")}
                    </Button>
                    <Button type="primary" onClick={applyModalSelection}>
                      OK
                    </Button>
                  </div>
                </div>
              </ModalWindow>
            </Portal>
          )}
        </DivE>
      )
    );
  }

  return wrapperForm(mainCascader);
}

export default RZcascader;
/* eslint-enable @typescript-eslint/no-unused-vars */
